import React from 'react';
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Globe, Eye, Heart, BookOpen } from "lucide-react";

// Optimized image component for lazy loading
const OptimizedImage = ({
  src,
  alt,
  className
}) => {
  return <img src={src} alt={alt} className={className} loading="lazy" decoding="async" onError={e => {
    e.currentTarget.onerror = null;
    e.currentTarget.src = "https://bbhhusjprzalcwvkgvsa.supabase.co/storage/v1/object/public/photos/placeholder-image.png";
  }} />;
};

const BibleTools = () => {
  // Define tool data for the cards
  const tools = [{
    image: "https://bbhhusjprzalcwvkgvsa.supabase.co/storage/v1/object/public/photos//ai%20navigator.png",
    title: "Scripture Navigator",
    description: "Find verses by topic with commentary",
    longDescription: "Find Bible verses by topic, with optional commentary to explore deeper meaning.",
    icon: Globe,
    linkTo: "/scripture-navigator"
  }, {
    image: "https://bbhhusjprzalcwvkgvsa.supabase.co/storage/v1/object/public/photos//ai%20deep%20diveee.png",
    title: "Deep Dive",
    description: "Detailed verse commentary",
    longDescription: "Find Bible verses by topic, with optional commentary to explore deeper meaning.",
    icon: Eye,
    linkTo: "/deep-dive"
  }, {
    image: "https://bbhhusjprzalcwvkgvsa.supabase.co/storage/v1/object/public/photos//ai%20words.png",
    title: "Etymology",
    description: "Original language insights",
    longDescription: "Find Bible verses by topic, with optional commentary to explore deeper meaning.",
    icon: BookOpen,
    // Changed from leaf to BookOpen which is a valid icon
    linkTo: "/etymology"
  }, {
    image: "https://bbhhusjprzalcwvkgvsa.supabase.co/storage/v1/object/public/photos//readingplannnjdjd.png",
    title: "Reading Plan",
    description: "Personalized Scripture journeys",
    longDescription: "Find Bible verses by topic, with optional commentary to explore deeper meaning.",
    icon: Heart,
    linkTo: "/reading-plan"
  }];
  
  return <div className="w-full bg-white">
      <div className="max-w-7xl mx-auto px-4 py-20">
        {/* Title Section */}
        <div className="text-center mb-8 py-[34px]">
          <h1 className="relative inline-block text-4xl sm:text-5xl md:text-7xl font-playfair font-bold mb-4">
            <span className="py-2 px-2 sm:px-8 block">
              Our Scripture <span className="text-heaven-gold">Tools</span>
            </span>
          </h1>
          <p className="text-blue-500 max-w-2xl mx-auto mt-6 text-base md:text-lg px-2">
            Explore our AI-powered Scripture tools designed to help you dive 
            deeper into God's Word with unprecedented insight and clarity.
          </p>
        </div>

        {/* Features List */}
        <div className="max-w-3xl mx-auto mb-4 px-2">
          <p className="text-center font-medium mb-2 text-sm sm:text-base">
            Unlike generic AI, our tools are uniquely trained on extensive biblical resources:
          </p>
          <ul className="space-y-2 text-center text-sm sm:text-base">
            <li className="flex items-center justify-center">
              <span className="mr-2">•</span>
              Biblical Hebrew and Greek original texts
            </li>
            <li className="flex items-center justify-center">
              <span className="mr-2">•</span>
              Comprehensive biblical commentaries
            </li>
            <li className="flex items-center justify-center">
              <span className="mr-2">•</span>
              Scholarly theological research
            </li>
            <li className="flex items-center justify-center">
              <span className="mr-2">•</span>
              Advanced contextual biblical hermeneutics
            </li>
          </ul>
        </div>

        {/* Tool Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {tools.map((tool, index) => <div key={tool.title} className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow duration-300 flex flex-col">
              <div className="mb-4 h-48 overflow-hidden rounded-md">
                <OptimizedImage src={tool.image} alt={tool.title} className="w-full h-full object-cover" />
              </div>
              <div className="flex justify-center mb-2">
                <div className="p-2 rounded-full bg-yellow-50">
                  <tool.icon className="h-6 w-6 text-heaven-gold" />
                </div>
              </div>
              <h3 className="text-xl text-blue-600 font-medium text-center mb-1">
                {tool.title}
              </h3>
              <p className="text-sm text-gray-600 text-center mb-4">
                {tool.description}
              </p>
              <p className="text-sm text-gray-700 text-center mb-6 flex-grow">
                {tool.longDescription}
              </p>
              <div className="mt-auto text-center">
                <Link to={tool.linkTo}>
                  {index === 0 ? <Button variant="outline" className="bg-black text-white hover:bg-gray-800 rounded-full px-6">
                      View Tool
                    </Button> : <Button variant="outline" className="bg-black text-white hover:bg-gray-800 rounded-full px-6">View Tool</Button>}
                </Link>
              </div>
            </div>)}
        </div>
      </div>
    </div>;
};
export default BibleTools;
